import { Formik, Form, useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Divider,
  Grid,
  Header,
  Icon,
  Message,
  Popup,
  Segment,
} from "semantic-ui-react";
import * as Yup from "yup";
import MyTextInput from "../../../app/common/form/MyTextInput";
import {
  addTransactionToDb,
  fetchTaskTemplatesFromDb,
  fetchManagerTaskTemplatesFromDb,
  fetchManagerFormTemplatesFromDb,
} from "../../../app/firestore/firestoreService";
import { toast } from "react-toastify";
import ModalWrapper from "../../../app/common/modals/modalWrapper";
import { closeModal } from "../../../app/common/modals/modalSlice";
import MyRadioButtons from "../../../app/common/form/MyRadioButtons";
import MyCheckbox from "../../../app/common/form/MyCheckbox";
import { agentRepresentsOptions, propertyTypeOptions } from "../../../app/common/categoryData/categoryOptions";
import MySelectInput from "../../../app/common/form/MySelectInput";
import {
  createPeopleOptions,
  filterPeopleByName,
  ensureHttpsImageUrl,
} from "../../../app/common/util/util";
import FormAddressAutofillGoogle from "../../../app/common/form/FormAddressAutofillGoogle";
import FormParty from "../../../app/common/form/FormParty";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import MyTextArea from "../../../app/common/form/MyTextArea";
import TransactionImportMlsForm from "./TransactionImportMlsForm";
import TransactionImportLegalAddressForm from "./TransactionImportLegalAddressForm";
import { fetchTaskTemplates } from "../../tasks/taskTemplates/taskTemplatesSlice";
import useFirestoreCollectionNoAsync from "../../../app/hooks/useFirestoreCollectionNoAsync";
import { onSnapshot } from "firebase/firestore";

// State to timezone mapping for North American timezones
const STATE_TZ = {
  CO: "America/Denver",
  UT: "America/Denver",
  NM: "America/Denver",
  WY: "America/Denver",
  AZ: "America/Phoenix", // no DST
  CA: "America/Los_Angeles",
  OR: "America/Los_Angeles",
  WA: "America/Los_Angeles",
  NV: "America/Los_Angeles",
  TX: "America/Chicago", // TX has 2 zones, but this is "close enough"
  IL: "America/Chicago",
  LA: "America/Chicago",
  OK: "America/Chicago",
  NY: "America/New_York",
  PA: "America/New_York",
  NC: "America/New_York",
  FL: "America/New_York",
};

// North American timezone options for dropdown
const TIMEZONE_OPTIONS = [
  { key: "America/New_York", value: "America/New_York", text: "Eastern Time (ET)" },
  { key: "America/Chicago", value: "America/Chicago", text: "Central Time (CT)" },
  { key: "America/Denver", value: "America/Denver", text: "Mountain Time (MT)" },
  { key: "America/Phoenix", value: "America/Phoenix", text: "Arizona Time (MST - No DST)" },
  { key: "America/Los_Angeles", value: "America/Los_Angeles", text: "Pacific Time (PT)" },
  { key: "America/Anchorage", value: "America/Anchorage", text: "Alaska Time (AKT)" },
  { key: "Pacific/Honolulu", value: "Pacific/Honolulu", text: "Hawaii Time (HST)" },
];

// Helper function to get timezone based on state
const getTimezoneFromState = (state) => {
  if (!state) return "";
  return STATE_TZ[state.toUpperCase()] || "";
};

// Helper function to create signer name format
const createSignerName = (firstName, middleName, lastName, authorityTitle) => {
  if (!firstName && !lastName) return "";

  let name = "";
  if (firstName) name += firstName;
  if (middleName) name += (name ? " " : "") + middleName;
  if (lastName) name += (name ? " " : "") + lastName;
  if (authorityTitle) name += ", " + authorityTitle;

  return name;
};

const poaDisplayOptions = [
  {
    key: "client_by_poa",
    value: "client_by_poa",
    text: "Client's name, by POA's name, as Attorney-in-Fact",
  },
  {
    key: "poa_for_client",
    value: "poa_for_client",
    text: "POA's name, as Attorney-in-Fact for Client's name",
  },
  {
    key: "custom",
    value: "custom",
    text: "Custom display (enter below)",
  },
];

export default function TransactionForm({ transactionId }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { transActive } = useSelector((state) => state.transaction);
  const { forms } = useSelector((state) => state.doc);
  const { formTemplates } = useSelector((state) => state.formTemplates);
  const { people } = useSelector((state) => state.people);
  const { currentUserProfile, agentsForAssistant, agentUserProfile } =
    useSelector((state) => state.profile);
  // const { taskTemplates } = useSelector((state) => state.taskTemplates); // Used in allTaskTemplates
  const [mlsData, setMlsData] = useState(null);
  const [legalAddressData, setLegalAddressData] = useState(null);
  const [managerTaskTemplates, setManagerTaskTemplates] = useState([]);
  const [allTaskTemplates, setAllTaskTemplates] = useState([]);
  const [managerFormTemplates, setManagerFormTemplates] = useState([]);

  const isMobile = useMediaQuery({ query: "(max-width:768px)" });

  const [agentOptions, setAgentOptions] = useState([]);
  const peopleOptions = createPeopleOptions(people, "Client");
  const isAssistant = currentUserProfile?.role === "assistant" ? true : false;
  const isTc = currentUserProfile?.role === "tc" ? true : false;
  const isManagerCreatingForAgent =
    currentUserProfile?.role === "manager" && agentUserProfile;
  const isManagerAssistantCreatingForAgent =
    currentUserProfile?.role === "managerassistant" && agentUserProfile;

  // Determine which profile to use for MLS info
  const profileForMlsInfo =
    isManagerCreatingForAgent || isManagerAssistantCreatingForAgent
      ? agentUserProfile
      : currentUserProfile;

  let initialValues = {
    active: true,
    queueNotifications:
      profileForMlsInfo?.notificationPreferences?.forceQueueNotifications ||
      profileForMlsInfo?.notificationPreferences?.queueNotificationsByDefault ||
      false,
    agentId: "",
    assistantEmail: "",
    assistant: {},
    hasAssistant: false,
    managerId: isManagerCreatingForAgent
      ? currentUserProfile.userId
      : currentUserProfile.managerId || "",
    agentRepresents: "Buyer",
    client: {
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      phone: "",
      isTrust: false,
      entityName: "",
      authorityTitle: "",
      signerName: "",
      sharedEmailClients: [],
      hasPoa: false,
      poaFullName: "",
      poaTitle: "",
      poaDisplayOption: "client_by_poa",
      poaCustomDisplay: "",
    },
    clientSecondary: {
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      phone: "",
      isTrust: false,
      entityName: "",
      authorityTitle: "",
      signerName: "",
      exists: false,
      sharesEmailWithPrimary: false,
      hasPoa: false,
      poaFullName: "",
      poaTitle: "",
      poaDisplayOption: "client_by_poa",
      poaCustomDisplay: "",
    },
    clientThird: {
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      phone: "",
      isTrust: false,
      entityName: "",
      authorityTitle: "",
      signerName: "",
      exists: false,
      sharesEmailWithPrimary: false,
      hasPoa: false,
      poaFullName: "",
      poaTitle: "",
      poaDisplayOption: "client_by_poa",
      poaCustomDisplay: "",
    },
    address: {
      street: "",
      unit: "",
      city: "",
      state: "",
      zipcode: "",
    },
    propertyDetails: {
      county: "",
      legalDescription: "",
      inclusions: "",
      exclusions: "",
      yearBuilt: "",
    },
    timeZone: "",
    title: "",
    addressGoogle: {
      address: "",
    },
    clientSelected: "",
    clientSecondarySelected: false,
    clientThirdSelected: false,
    state: "No Offer",
    stateProvince: "",
    closingDateTime: "",
    contractDateTime: "",
    listingContractDateTime: "",
    salesPrice: "",
    mlsNum: "",
    mlsNumbers: [],
    mlsIdCode: profileForMlsInfo.mlsAccess?.[0]?.mlsIdCode || "",
    mlsInfo: [
      {
        mlsIdCode: profileForMlsInfo.mlsAccess?.[0]?.mlsIdCode || "",
        mlsName: profileForMlsInfo.mlsAccess?.[0]?.mlsName || "",
        mlsNumber: "",
      },
      {
        mlsIdCode: profileForMlsInfo.mlsAccess?.[1]?.mlsIdCode || "",
        mlsName: profileForMlsInfo.mlsAccess?.[1]?.mlsName || "",
        mlsNumber: "",
      },
    ],
    role: "Transaction",
    pic: "",
    owner: {
      firstName: "",
      middleName: "",
      lastName: "",
      isTrust: false,
      entityName: "",
      authorityTitle: "",
      signerName: "",
    },
    owner2: {
      firstName: "",
      middleName: "",
      lastName: "",
      isTrust: false,
      entityName: "",
      authorityTitle: "",
      signerName: "",
      exists: false,
    },
    hasTc: false,
    propertyType: "Residential",
  };

  // Fetch user's own task templates
  useFirestoreCollectionNoAsync({
    query: () => fetchTaskTemplatesFromDb(),
    data: (userTemplates) => {
      const templatesWithSource = userTemplates.map((template) => ({
        ...template,
        templateSource: "own",
      }));

      // Combine with manager templates
      const combined = [...templatesWithSource, ...managerTaskTemplates];
      dispatch(fetchTaskTemplates(combined));
      setAllTaskTemplates(combined);
    },
    deps: [dispatch, managerTaskTemplates],
  });

  // Fetch manager task templates if user is an agent with a manager
  useEffect(() => {
    if (currentUserProfile?.role === "agent" && currentUserProfile?.managerId) {
      const unsubscribe = onSnapshot(
        fetchManagerTaskTemplatesFromDb(currentUserProfile.managerId),
        (snapshot) => {
          const managerTemplatesData = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
            templateSource: "manager",
          }));
          setManagerTaskTemplates(managerTemplatesData);
        },
        (error) => {
          console.error("Error fetching manager templates:", error);
        }
      );

      return () => unsubscribe();
    } else {
      setManagerTaskTemplates([]);
    }
  }, [currentUserProfile]);

  // Fetch manager form templates if user is an agent with a manager
  useEffect(() => {
    if (currentUserProfile?.role === "agent" && currentUserProfile?.managerId) {
      const unsubscribe = onSnapshot(
        fetchManagerFormTemplatesFromDb(currentUserProfile.managerId),
        (snapshot) => {
          const managerFormTemplatesData = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
            templateSource: "manager",
          }));
          setManagerFormTemplates(managerFormTemplatesData);
        },
        (error) => {
          console.error("Error fetching manager form templates:", error);
        }
      );
      return () => unsubscribe();
    } else {
      setManagerFormTemplates([]);
    }
  }, [currentUserProfile]);

  useEffect(() => {
    if (!isAssistant) {
      return;
    } else {
      let agentList = [];
      agentsForAssistant.forEach((agent) => {
        const agentName = agent.firstName + " " + agent.lastName;
        console.log("agentName", agentName);
        agentList = [
          ...agentList,
          {
            key: agent.userId,
            value: agent.userId,
            text: agentName,
          },
        ];
      });
      setAgentOptions(agentList);
    }
  }, [isAssistant, agentsForAssistant]);

  if (transactionId) {
    initialValues = transActive.filter(
      (trans) => trans.id === transactionId
    )[0];
  }

  const [clientSecondaryExistsField, setClientSecondaryExistsField] = useState(
    initialValues.clientSecondary.exists ? true : false
  );
  const [clientThirdExistsField, setClientThirdExistsField] = useState(
    initialValues.clientThird?.exists ? true : false
  );
  const [clientIsTrustField, setClientIsTrustField] = useState(
    initialValues.client?.isTrust ? true : false
  );
  const [clientSecondaryIsTrustField, setClientSecondaryIsTrustField] =
    useState(initialValues.clientSecondary?.isTrust ? true : false);

  const [clientThirdIsTrustField, setClientThirdIsTrustField] = useState(
    initialValues.clientThird?.isTrust ? true : false
  );

  const [clientHasPoaField, setClientHasPoaField] = useState(
    initialValues.client?.hasPoa ? true : false
  );
  const [clientSecondaryHasPoaField, setClientSecondaryHasPoaField] =
    useState(initialValues.clientSecondary?.hasPoa ? true : false);
  const [clientThirdHasPoaField, setClientThirdHasPoaField] = useState(
    initialValues.clientThird?.hasPoa ? true : false
  );

  const validationSchema = Yup.object()
    .shape({
      agentId: Yup.string().concat(
        isAssistant || isTc
          ? Yup.string().required("Must select an agent for the transaction")
          : Yup.string()
      ),
      agentRepresents: Yup.string().required("You must choose one"),
      title: Yup.string().required("You must name the transaction"),
      client: Yup.object().shape({
        email: Yup.string().email("Must be a valid email"),
        firstName: Yup.string().required("First name is required"),
        lastName: Yup.string().required("Last name is required"),
        poaFullName: Yup.string().when("hasPoa", {
          is: true,
          then: (schema) => schema.required("POA full name is required"),
        }),
        poaTitle: Yup.string().when("hasPoa", {
          is: true,
          then: (schema) => schema.required("POA title is required"),
        }),
        poaCustomDisplay: Yup.string().when(["hasPoa", "poaDisplayOption"], {
          is: (hasPoa, poaDisplayOption) =>
            hasPoa && poaDisplayOption === "custom",
          then: (schema) => schema.required("Custom display text is required"),
        }),
      }),
      clientSecondary: Yup.object().shape({
        exists: Yup.boolean(),
        email: Yup.string().when("exists", {
          is: true,
          then: Yup.string().email("Must be a valid email"),
        }),
        firstName: Yup.string().when("exists", {
          is: true,
          then: Yup.string().required("First name is required"),
        }),
        lastName: Yup.string().when("exists", {
          is: true,
          then: Yup.string().required("Last name is required"),
        }),
        poaFullName: Yup.string().when("hasPoa", {
          is: true,
          then: (schema) => schema.required("POA full name is required"),
        }),
        poaTitle: Yup.string().when("hasPoa", {
          is: true,
          then: (schema) => schema.required("POA title is required"),
        }),
        poaCustomDisplay: Yup.string().when(["hasPoa", "poaDisplayOption"], {
          is: (hasPoa, poaDisplayOption) =>
            hasPoa && poaDisplayOption === "custom",
          then: (schema) => schema.required("Custom display text is required"),
        }),
      }),
      clientThird: Yup.object().shape({
        exists: Yup.boolean(),
        email: Yup.string().when("exists", {
          is: true,
          then: Yup.string().email("Must be a valid email"),
        }),
        firstName: Yup.string().when("exists", {
          is: true,
          then: Yup.string().required("First name is required"),
        }),
        lastName: Yup.string().when("exists", {
          is: true,
          then: Yup.string().required("Last name is required"),
        }),
        poaFullName: Yup.string().when("hasPoa", {
          is: true,
          then: (schema) => schema.required("POA full name is required"),
        }),
        poaTitle: Yup.string().when("hasPoa", {
          is: true,
          then: (schema) => schema.required("POA title is required"),
        }),
        poaCustomDisplay: Yup.string().when(["hasPoa", "poaDisplayOption"], {
          is: (hasPoa, poaDisplayOption) =>
            hasPoa && poaDisplayOption === "custom",
          then: (schema) => schema.required("Custom display text is required"),
        }),
      }),
    })
    .test({
      name: "notOneOf",
      test: function (values) {
        // Allow secondary/tertiary clients to share email with primary if checkbox is checked
        const secondarySharesWithPrimary =
          values?.clientSecondary?.sharesEmailWithPrimary === true;
        const thirdSharesWithPrimary =
          values?.clientThird?.sharesEmailWithPrimary === true;

        const isValid =
          // Check client vs clientSecondary (allow if checkbox is checked)
          (!values?.client?.email ||
            !values?.clientSecondary?.email ||
            values?.client?.email !== values?.clientSecondary?.email ||
            secondarySharesWithPrimary) &&
          // Client email can't be the current user's email
          values?.client?.email !== currentUserProfile.email &&
          // ClientSecondary email can't be the current user's email
          values?.clientSecondary?.email !== currentUserProfile.email &&
          // Check client vs clientThird (allow if checkbox is checked)
          (!values?.clientThird?.email ||
            values?.client?.email !== values?.clientThird?.email ||
            thirdSharesWithPrimary) &&
          // ClientSecondary and ClientThird can't share email (they must both share with primary, not each other)
          (!values?.clientSecondary?.email ||
            !values?.clientThird?.email ||
            values?.clientSecondary?.email !== values?.clientThird?.email);
        if (isValid) return true;
        return this.createError({
          path: "client.email",
          message: "Emails can't be the same as another party",
        });
      },
    });

  function Watcher() {
    const { values, setFieldValue, touched } = useFormikContext();

    useEffect(() => {
      if (
        !touched.title &&
        (values.client?.lastName || values.address?.street)
      ) {
        const newTitle =
          values.client?.lastName && values.address?.street
            ? values.client?.lastName + " - " + values.address?.street
            : values.client?.street
            ? values.client?.street
            : values.client?.lastName;
        setFieldValue("title", newTitle);
      }
    }, [values, touched, setFieldValue]);

    // Update signer names when name fields or authority title change
    useEffect(() => {
      if (values.client?.isTrust) {
        const signerName = createSignerName(
          values.client?.firstName,
          values.client?.middleName,
          values.client?.lastName,
          values.client?.authorityTitle || ""
        );
        setFieldValue("client.signerName", signerName);
      } else {
        setFieldValue("client.signerName", "");
      }
    }, [values.client, setFieldValue]);

    useEffect(() => {
      if (values.clientSecondary?.isTrust) {
        const signerName = createSignerName(
          values.clientSecondary?.firstName,
          values.clientSecondary?.middleName,
          values.clientSecondary?.lastName,
          values.clientSecondary?.authorityTitle || ""
        );
        setFieldValue("clientSecondary.signerName", signerName);
      } else {
        setFieldValue("clientSecondary.signerName", "");
      }
    }, [values.clientSecondary, setFieldValue]);

    useEffect(() => {
      if (values.clientThird?.isTrust) {
        const signerName = createSignerName(
          values.clientThird?.firstName,
          values.clientThird?.middleName,
          values.clientThird?.lastName,
          values.clientThird?.authorityTitle || ""
        );
        setFieldValue("clientThird.signerName", signerName);
      } else {
        setFieldValue("clientThird.signerName", "");
      }
    }, [values.clientThird, setFieldValue]);

    useEffect(() => {
      if (values.clientSelected) {
        const selectedClient = filterPeopleByName(
          people,
          values.clientSelected?.split("|")[0],
          values.clientSelected?.split("|")[1]
        );
        if (selectedClient) {
          const clientFirstName = selectedClient.firstName
            ? selectedClient.firstName
            : "";
          setFieldValue("client.firstName", clientFirstName);
          const clientMiddleName = selectedClient.middleName
            ? selectedClient.middleName
            : "";
          setFieldValue("client.middleName", clientMiddleName);
          const clientLastName = selectedClient.lastName
            ? selectedClient.lastName
            : "";
          setFieldValue("client.lastName", clientLastName);
          const clientEmail = selectedClient.email ? selectedClient.email : "";
          setFieldValue("client.email", clientEmail);
          const clientPhone = selectedClient.phone ? selectedClient.phone : "";
          setFieldValue("client.phone", clientPhone);
          setFieldValue("clientSelected", "");
          if (selectedClient.isTrust && selectedClient.entityName) {
            setClientIsTrustField(true);
            setFieldValue("client.isTrust", true);
            setFieldValue("client.entityName", selectedClient.entityName);
            setFieldValue(
              "client.authorityTitle",
              selectedClient.authorityTitle || ""
            );
            setFieldValue("client.signerName", selectedClient.signerName || "");
          } else {
            setClientIsTrustField(false);
            setFieldValue("client.isTrust", false);
            setFieldValue("client.entityName", "");
            setFieldValue("client.authorityTitle", "");
            setFieldValue("client.signerName", "");
          }
        }
      }
      if (values.clientSecondarySelected) {
        const selectedSecondaryClient = filterPeopleByName(
          people,
          values.clientSecondarySelected?.split("|")[0],
          values.clientSecondarySelected?.split("|")[1]
        );
        if (selectedSecondaryClient) {
          const clientSecondaryFirstName = selectedSecondaryClient.firstName
            ? selectedSecondaryClient.firstName
            : "";
          setFieldValue("clientSecondary.firstName", clientSecondaryFirstName);
          const clientSecondaryMiddleName = selectedSecondaryClient.middleName
            ? selectedSecondaryClient.middleName
            : "";
          setFieldValue(
            "clientSecondary.middleName",
            clientSecondaryMiddleName
          );
          const clientSecondaryLastName = selectedSecondaryClient.lastName
            ? selectedSecondaryClient.lastName
            : "";
          setFieldValue("clientSecondary.lastName", clientSecondaryLastName);
          const clientSecondaryEmail = selectedSecondaryClient.email
            ? selectedSecondaryClient.email
            : "";
          setFieldValue("clientSecondary.email", clientSecondaryEmail);
          const clientSecondaryPhone = selectedSecondaryClient.phone
            ? selectedSecondaryClient.phone
            : "";
          setFieldValue("clientSecondary.phone", clientSecondaryPhone);
          setFieldValue("clientSecondarySelected", "");
          if (
            selectedSecondaryClient.isTrust &&
            selectedSecondaryClient.entityName
          ) {
            setClientSecondaryIsTrustField(true);
            setFieldValue("clientSecondary.isTrust", true);
            setFieldValue(
              "clientSecondary.entityName",
              selectedSecondaryClient.entityName
            );
            setFieldValue(
              "clientSecondary.authorityTitle",
              selectedSecondaryClient.authorityTitle || ""
            );
            setFieldValue(
              "clientSecondary.signerName",
              selectedSecondaryClient.signerName || ""
            );
          } else {
            setClientSecondaryIsTrustField(false);
            setFieldValue("clientSecondary.isTrust", false);
            setFieldValue("clientSecondary.entityName", "");
            setFieldValue("clientSecondary.authorityTitle", "");
            setFieldValue("clientSecondary.signerName", "");
          }
        }
      }
      if (values.clientThirdSelected) {
        const selectedThirdClient = filterPeopleByName(
          people,
          values.clientThirdSelected?.split("|")[0],
          values.clientThirdSelected?.split("|")[1]
        );
        if (selectedThirdClient) {
          const clientThirdFirstName = selectedThirdClient.firstName
            ? selectedThirdClient.firstName
            : "";
          setFieldValue("clientThird.firstName", clientThirdFirstName);
          const clientThirdMiddleName = selectedThirdClient.middleName
            ? selectedThirdClient.middleName
            : "";
          setFieldValue("clientThird.middleName", clientThirdMiddleName);
          const clientThirdLastName = selectedThirdClient.lastName
            ? selectedThirdClient.lastName
            : "";
          setFieldValue("clientThird.lastName", clientThirdLastName);
          const clientThirdEmail = selectedThirdClient.email
            ? selectedThirdClient.email
            : "";
          setFieldValue("clientThird.email", clientThirdEmail);
          const clientThirdPhone = selectedThirdClient.phone
            ? selectedThirdClient.phone
            : "";
          setFieldValue("clientThird.phone", clientThirdPhone);
          setFieldValue("clientThirdSelected", "");
          if (selectedThirdClient.isTrust && selectedThirdClient.entityName) {
            setClientThirdIsTrustField(true);
            setFieldValue("clientThird.isTrust", true);
            setFieldValue(
              "clientThird.entityName",
              selectedThirdClient.entityName
            );
            setFieldValue(
              "clientThird.authorityTitle",
              selectedThirdClient.authorityTitle || ""
            );
            setFieldValue(
              "clientThird.signerName",
              selectedThirdClient.signerName || ""
            );
          } else {
            setClientThirdIsTrustField(false);
            setFieldValue("clientThird.isTrust", false);
            setFieldValue("clientThird.entityName", "");
            setFieldValue("clientThird.authorityTitle", "");
            setFieldValue("clientThird.signerName", "");
          }
        }
      }
    }, [
      values.clientSelected,
      values.clientSecondarySelected,
      values.clientThirdSelected,
      setFieldValue,
    ]);

    useEffect(() => {
      if (legalAddressData) {
        setFieldValue("propertyDetails.legalDescription", legalAddressData);
      }
    }, [setFieldValue]);

    useEffect(() => {
      if (mlsData) {
        if (mlsData.address?.street) {
          setFieldValue("address.street", mlsData.address.street);
        }
        if (mlsData.address?.unit) {
          setFieldValue("address.unit", mlsData.address.unit);
        }
        if (mlsData.address?.city) {
          setFieldValue("address.city", mlsData.address.city);
        }
        if (mlsData.address?.state) {
          setFieldValue("address.state", mlsData.address.state);
        }
        if (mlsData.address?.zipcode) {
          setFieldValue("address.zipcode", mlsData.address.zipcode);
        }
        if (mlsData.propertyDetails?.legalDescription) {
          setFieldValue(
            "propertyDetails.legalDescription",
            mlsData.propertyDetails.legalDescription
          );
        }
        if (mlsData.propertyDetails?.county) {
          setFieldValue(
            "propertyDetails.county",
            mlsData.propertyDetails.county
          );
        }
        if (mlsData.propertyDetails?.inclusions) {
          setFieldValue(
            "propertyDetails.inclusions",
            mlsData.propertyDetails.inclusions
          );
        }
        if (mlsData.propertyDetails?.exclusions) {
          setFieldValue(
            "propertyDetails.exclusions",
            mlsData.propertyDetails.exclusions
          );
        }
        if (mlsData.propertyDetails?.yearBuilt) {
          setFieldValue(
            "propertyDetails.yearBuilt",
            mlsData.propertyDetails.yearBuilt
          );
        }
        // Structural facts from the live MLS feed - used by the Strategy
        // feature's pricing analysis. Only set when the MLS actually
        // populated them.
        [
          "publicRemarks",
          "bedroomsTotal",
          "bathroomsFull",
          "bathroomsHalf",
          "bathroomsThreeQuarter",
          "aboveGradeFinishedSqft",
          "belowGradeFinishedSqft",
          "belowGradeUnfinishedSqft",
          "basement",
          "garageSpaces",
          "lotSizeSqft",
          "levels",
          "propertySubType",
          "view",
          "waterfrontYN",
          "associationFee",
          "associationFeeIncludes",
        ].forEach((field) => {
          const value = mlsData.propertyDetails?.[field];
          const hasValue = Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== "";
          if (hasValue) {
            setFieldValue(`propertyDetails.${field}`, value);
          }
        });
        if (values?.mlsDataOptions?.useMlsPic && mlsData.pic) {
          setFieldValue("pic", ensureHttpsImageUrl(mlsData.pic));
        } else {
          setFieldValue("pic", "");
        }
      }
    }, [values.mlsDataOptions, setFieldValue]);

    // Auto-select timezone based on state
    useEffect(() => {
      if (values.address?.state && !values.timeZone) {
        const suggestedTimezone = getTimezoneFromState(values.address.state);
        if (suggestedTimezone) {
          setFieldValue("timeZone", suggestedTimezone);
        }
      }
    }, [values.address.state, values.timeZone, setFieldValue]);

    return null;
  }

  function formSelectAgent() {
    return (
      <>
        <Segment className="background-lightgrey">
          <div className="zero bottom margin">
            <Grid>
              <Grid.Row>
                <Grid.Column mobile={16} computer={4}>
                  <h2 className="text blue">Transaction Agent</h2>
                  <MySelectInput
                    name="agentId"
                    placeholder="Select Agent"
                    options={agentOptions}
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </div>
        </Segment>
      </>
    );
  }

  function formWhoRepresenting() {
    return (
      <Segment className="background-lightgrey">
        <div className="zero bottom margin">
          <Grid>
            <Grid.Column width={16}>
              <h2 className="text blue">I'm representing the</h2>
              <MyRadioButtons
                name="agentRepresents"
                placeholder=""
                options={agentRepresentsOptions}
              />
            </Grid.Column>
          </Grid>
        </div>
      </Segment>
    );
  }

  function resLandCommForeclosure() {
    // "All" is a valid value for template/form assignment rules (see
    // TaskTemplatesEdit, AdminCreateFormModal, AdminComplianceTemplateModal)
    // but isn't a real property type for an actual transaction, so it's
    // excluded here.
    const transactionPropertyTypeOptions = propertyTypeOptions.filter((option) => option.value !== "All");
    return (
      <Segment className="background-lightgrey">
        <div className="zero bottom margin">
          <Grid>
            <Grid.Column width={16}>
              <h2 className="text blue">Property Type</h2>
              <MyRadioButtons
                name="propertyType"
                placeholder=""
                options={transactionPropertyTypeOptions}
              />
            </Grid.Column>
          </Grid>
        </div>
      </Segment>)
  }

  function formClients(values) {
    return (
      <Segment className="background-lightgrey">
        <div className="small bottom margin">
          <Grid>
            <Grid.Row>
              <Grid.Column width={16}>
                <h2 className="text blue">Primary Client</h2>
                <p>Choose an existing client or create a new one.</p>
              </Grid.Column>
              <Grid.Column mobile={16} computer={4}>
                <MySelectInput
                  name="clientSelected"
                  placeholder="Select existing client"
                  options={peopleOptions}
                  search
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <FormParty fieldKey="client." newPerson={true} />
          <Grid>
            <Grid.Row className="tiny vertical padding">
              <Grid.Column width={16}>
                <MyCheckbox
                  name="client.isTrust"
                  label="The client is a company, trust, or other entity"
                  onClick={() => setClientIsTrustField(!clientIsTrustField)}
                />
              </Grid.Column>
            </Grid.Row>
            {clientIsTrustField && (
              <>
                <Grid.Row className="zero top padding small bottom padding">
                  <Grid.Column mobile={16} computer={10}>
                    <MyTextInput
                      name="client.entityName"
                      label="Company, trust, or entity name"
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row className="zero top padding small bottom padding">
                  <Grid.Column mobile={16} computer={8}>
                    <MyTextInput
                      name="client.authorityTitle"
                      label="Authority title (e.g., Manager, Trustee, President, Member)"
                      placeholder="Manager"
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row className="zero top padding small bottom padding">
                  <Grid.Column mobile={16} computer={10}>
                    <MyTextInput
                      name="client.signerName"
                      label="Signer name (auto-generated)"
                      readOnly
                      style={{ backgroundColor: "#f8f8f8" }}
                    />
                  </Grid.Column>
                </Grid.Row>
              </>
            )}
            {values?.agentRepresents === "Seller" && (
              <>
                <Grid.Row className="tiny vertical padding">
                  <Grid.Column width={16}>
                    <MyCheckbox
                      name="client.hasPoa"
                      label="This client will be represented by a Power of Attorney (POA)"
                      onClick={() => setClientHasPoaField(!clientHasPoaField)}
                    />
                  </Grid.Column>
                </Grid.Row>
                {clientHasPoaField && (
                  <>
                    <Grid.Row>
                      <Grid.Column width={16}>
                        <p>
                          The email above should be for the power of attorney
                          individual.
                          <br />
                          <i>
                            If you want to share documents to your client who
                            the POA represents, add your client's email to
                            "Other Parties" using the "Other" role and click
                            their button to share documents with them.
                          </i>
                        </p>
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="zero top padding small bottom padding">
                      <Grid.Column mobile={16} computer={8}>
                        <MyTextInput
                          name="client.poaFullName"
                          label="POA's Full Name"
                          placeholder="Enter POA's full name"
                        />
                      </Grid.Column>
                      <Grid.Column mobile={16} computer={8}>
                        <MyTextInput
                          name="client.poaTitle"
                          label="POA's Title"
                          placeholder="Enter POA's title"
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="zero top padding small bottom padding">
                      <Grid.Column width={16}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "8px",
                          }}
                        >
                          <label style={{ marginRight: "8px" }}>
                            Signature Display Option
                          </label>
                          <Popup
                            trigger={<Icon name="info circle" color="blue" />}
                            content={
                              <div>
                                <p>
                                  <strong>
                                    Examples of how signatures will display:
                                  </strong>
                                </p>
                                <p>
                                  <strong>Option A:</strong> John Q.
                                  Principal, by Jane R. Agent, as
                                  Attorney-in-Fact
                                </p>
                                <p>
                                  <strong>Option B:</strong> Jane R. Agent, as
                                  Attorney-in-Fact for John Q. Principal
                                </p>
                                <p>
                                  <strong>Custom:</strong> You can enter your
                                  own display format
                                </p>
                                <br />
                                <p>
                                  <strong>Note:</strong> The email for this
                                  client should be the POA's email address.
                                </p>
                                <p>
                                  <strong>Initials will display as:</strong>{" "}
                                  Client's initials by POA's initials (AIF)
                                </p>
                                <p>
                                  <strong>Example:</strong> JQP by JRA (AIF)
                                </p>
                              </div>
                            }
                            wide="very"
                            position="top center"
                          />
                        </div>
                        <MySelectInput
                          name="client.poaDisplayOption"
                          options={poaDisplayOptions}
                          placeholder="Select display option"
                        />
                      </Grid.Column>
                    </Grid.Row>
                    {values?.client?.poaDisplayOption === "custom" && (
                      <Grid.Row className="zero top padding small bottom padding">
                        <Grid.Column width={16}>
                          <MyTextInput
                            name="client.poaCustomDisplay"
                            label="Custom Signature Display"
                            placeholder="Enter how you want the signature to display"
                          />
                        </Grid.Column>
                      </Grid.Row>
                    )}
                  </>
                )}
              </>
            )}
            <Grid.Row className="tiny top padding tiny bottom padding">
              <Grid.Column width={16}>
                <MyCheckbox
                  name="clientSecondary.exists"
                  label="There is another client for this transaction"
                  onClick={() =>
                    setClientSecondaryExistsField(!clientSecondaryExistsField)
                  }
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
          {clientSecondaryExistsField && (
            <>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <h2 className="text blue">Secondary Client</h2>
                    <p>Choose an existing client or create a new one</p>
                  </Grid.Column>
                  <Grid.Column mobile={16} computer={5}>
                    <MySelectInput
                      name="clientSecondarySelected"
                      placeholder="Select existing client"
                      options={peopleOptions}
                      search
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
              <FormParty fieldKey="clientSecondary." newPerson={true} />
              <Grid>

                <Grid.Row className="tiny vertical padding">
                  <Grid.Column width={16}>
                    <MyCheckbox
                      name="clientSecondary.sharesEmailWithPrimary"
                      label={`Client shares email with primary ${values?.agentRepresents === "Seller" ? "seller" : "buyer"}`}
                    />
                  </Grid.Column>
                </Grid.Row>
                
                <Grid.Row className="tiny vertical padding">
                  <Grid.Column width={16}>
                    <MyCheckbox
                      name="clientSecondary.isTrust"
                      label="The secondary client is a company, trust, or other
                        entity"
                      onClick={() =>
                        setClientSecondaryIsTrustField(
                          !clientSecondaryIsTrustField
                        )
                      }
                    />
                  </Grid.Column>
                </Grid.Row>


                {clientSecondaryIsTrustField && (
                  <>
                    <Grid.Row className="zero top padding small bottom padding">
                      <Grid.Column mobile={16} computer={8}>
                        <MyTextInput
                          name="clientSecondary.entityName"
                          label="Company, trust, or entity name"
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="zero top padding small bottom padding">
                      <Grid.Column mobile={16} computer={8}>
                        <MyTextInput
                          name="clientSecondary.authorityTitle"
                          label="Authority title (e.g., Manager, Trustee, President, Member)"
                          placeholder="Manager"
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="zero top padding small bottom padding">
                      <Grid.Column mobile={16} computer={10}>
                        <MyTextInput
                          name="clientSecondary.signerName"
                          label="Signer name (auto-generated)"
                          readOnly
                          style={{ backgroundColor: "#f8f8f8" }}
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </>
                )}
                {values?.agentRepresents === "Seller" && (
                  <>
                    <Grid.Row className="tiny vertical padding">
                      <Grid.Column width={16}>
                        <MyCheckbox
                          name="clientSecondary.hasPoa"
                          label="This client will be represented by a Power of Attorney (POA)"
                          onClick={() =>
                            setClientSecondaryHasPoaField(
                              !clientSecondaryHasPoaField
                            )
                          }
                        />
                      </Grid.Column>
                    </Grid.Row>
                    {clientSecondaryHasPoaField && (
                      <>
                        <Grid.Row>
                          <Grid.Column width={16}>
                            <p>
                              The email above should be for the power of
                              attorney individual.
                              <br />
                              <i>
                                If you want to share documents to your client
                                who the POA represents, add your client's
                                email to "Other Parties" using the "Other"
                                role and click their button to share
                                documents with them.
                              </i>
                            </p>
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row className="zero top padding small bottom padding">
                          <Grid.Column mobile={16} computer={8}>
                            <MyTextInput
                              name="clientSecondary.poaFullName"
                              label="POA's Full Name"
                              placeholder="Enter POA's full name"
                            />
                          </Grid.Column>
                          <Grid.Column mobile={16} computer={8}>
                            <MyTextInput
                              name="clientSecondary.poaTitle"
                              label="POA's Title"
                              placeholder="Enter POA's title"
                            />
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row className="zero top padding small bottom padding">
                          <Grid.Column width={16}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "8px",
                              }}
                            >
                              <label style={{ marginRight: "8px" }}>
                                Signature Display Option
                              </label>
                              <Popup
                                trigger={
                                  <Icon name="info circle" color="blue" />
                                }
                                content={
                                  <div>
                                    <p>
                                      <strong>
                                        Examples of how signatures will
                                        display:
                                      </strong>
                                    </p>
                                    <p>
                                      <strong>Option A:</strong> John Q.
                                      Principal, by Jane R. Agent, as
                                      Attorney-in-Fact
                                    </p>
                                    <p>
                                      <strong>Option B:</strong> Jane R.
                                      Agent, as Attorney-in-Fact for John Q.
                                      Principal
                                    </p>
                                    <p>
                                      <strong>Custom:</strong> You can enter
                                      your own display format
                                    </p>
                                    <br />
                                    <p>
                                      <strong>Note:</strong> The email for
                                      this client should be the POA's email
                                      address.
                                    </p>
                                    <p>
                                      <strong>
                                        Initials will display as:
                                      </strong>{" "}
                                      Client's initials by POA's initials
                                      (AIF)
                                    </p>
                                    <p>
                                      <strong>Example:</strong> JQP by JRA
                                      (AIF)
                                    </p>
                                  </div>
                                }
                                wide="very"
                                position="top center"
                              />
                            </div>
                            <MySelectInput
                              name="clientSecondary.poaDisplayOption"
                              options={poaDisplayOptions}
                              placeholder="Select display option"
                            />
                          </Grid.Column>
                        </Grid.Row>
                        {values?.clientSecondary?.poaDisplayOption ===
                          "custom" && (
                          <Grid.Row className="zero top padding small bottom padding">
                            <Grid.Column width={16}>
                              <MyTextInput
                                name="clientSecondary.poaCustomDisplay"
                                label="Custom Signature Display"
                                placeholder="Enter how you want the signature to display"
                              />
                            </Grid.Column>
                          </Grid.Row>
                        )}
                      </>
                    )}
                  </>
                )}
                <Grid.Row className="tiny top padding tiny bottom padding">
                  <Grid.Column width={16}>
                    <MyCheckbox
                      name="clientThird.exists"
                      label="There is another client for this transaction"
                      onClick={() =>
                        setClientThirdExistsField(!clientThirdExistsField)
                      }
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </>
          )}
          {clientSecondaryExistsField && clientThirdExistsField && (
            <>
              <Grid>
                <Grid.Row>
                  <Grid.Column width={16}>
                    <h2 className="text blue">Third Client</h2>
                    <p>Choose an existing client or create a new one</p>
                  </Grid.Column>
                  <Grid.Column mobile={16} computer={5}>
                    <MySelectInput
                      name="clientThirdSelected"
                      placeholder="Select existing client"
                      options={peopleOptions}
                      search
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
              <FormParty fieldKey="clientThird." newPerson={true} />
              <Grid>
                <Grid.Row className="tiny vertical padding">
                  <Grid.Column width={16}>
                    <MyCheckbox
                      name="clientThird.sharesEmailWithPrimary"
                      label={`Client shares email with primary ${values?.agentRepresents === "Seller" ? "seller" : "buyer"}`}
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row className="tiny vertical padding">
                  <Grid.Column width={16}>
                    <MyCheckbox
                      name="clientThird.isTrust"
                      label="The third client is a company, trust, or other
                        entity"
                      onClick={() =>
                        setClientThirdIsTrustField(!clientThirdIsTrustField)
                      }
                    />
                  </Grid.Column>
                </Grid.Row>

                {clientThirdIsTrustField && (
                  <>
                    <Grid.Row className="zero top padding small bottom padding">
                      <Grid.Column mobile={16} computer={8}>
                        <MyTextInput
                          name="clientThird.entityName"
                          label="Company, trust, or entity name"
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="zero top padding small bottom padding">
                      <Grid.Column mobile={16} computer={8}>
                        <MyTextInput
                          name="clientThird.authorityTitle"
                          label="Authority title (e.g., Manager, Trustee, President, Member)"
                          placeholder="Manager"
                        />
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className="zero top padding small bottom padding">
                      <Grid.Column mobile={16} computer={10}>
                        <MyTextInput
                          name="clientThird.signerName"
                          label="Signer name (auto-generated)"
                          readOnly
                          style={{ backgroundColor: "#f8f8f8" }}
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </>
                )}
                {values?.agentRepresents === "Seller" && (
                  <>
                    <Grid.Row className="tiny vertical padding">
                      <Grid.Column width={16}>
                        <MyCheckbox
                          name="clientThird.hasPoa"
                          label="This client will be represented by a Power of Attorney (POA)"
                          onClick={() =>
                            setClientThirdHasPoaField(!clientThirdHasPoaField)
                          }
                        />
                      </Grid.Column>
                    </Grid.Row>
                    {clientThirdHasPoaField && (
                      <>
                        <Grid.Row>
                          <Grid.Column width={16}>
                            <p>
                              The email above should be for the power of
                              attorney individual.
                              <br />
                              <i>
                                If you want to share documents to your client
                                who the POA represents, add your client's
                                email to "Other Parties" using the "Other"
                                role and click their button to share
                                documents with them.
                              </i>
                            </p>
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row className="zero top padding small bottom padding">
                          <Grid.Column mobile={16} computer={8}>
                            <MyTextInput
                              name="clientThird.poaFullName"
                              label="POA's Full Name"
                              placeholder="Enter POA's full name"
                            />
                          </Grid.Column>
                          <Grid.Column mobile={16} computer={8}>
                            <MyTextInput
                              name="clientThird.poaTitle"
                              label="POA's Title"
                              placeholder="Enter POA's title"
                            />
                          </Grid.Column>
                        </Grid.Row>
                        <Grid.Row className="zero top padding small bottom padding">
                          <Grid.Column width={16}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: "8px",
                              }}
                            >
                              <label style={{ marginRight: "8px" }}>
                                Signature Display Option
                              </label>
                              <Popup
                                trigger={
                                  <Icon name="info circle" color="blue" />
                                }
                                content={
                                  <div>
                                    <p>
                                      <strong>
                                        Examples of how signatures will
                                        display:
                                      </strong>
                                    </p>
                                    <p>
                                      <strong>Option A:</strong> John Q.
                                      Principal, by Jane R. Agent, as
                                      Attorney-in-Fact
                                    </p>
                                    <p>
                                      <strong>Option B:</strong> Jane R.
                                      Agent, as Attorney-in-Fact for John Q.
                                      Principal
                                    </p>
                                    <p>
                                      <strong>Custom:</strong> You can enter
                                      your own display format
                                    </p>
                                    <br />
                                    <p>
                                      <strong>Note:</strong> The email for
                                      this client should be the POA's email
                                      address.
                                    </p>
                                    <p>
                                      <strong>
                                        Initials will display as:
                                      </strong>{" "}
                                      Client's initials by POA's initials
                                      (AIF)
                                    </p>
                                    <p>
                                      <strong>Example:</strong> JQP by JRA
                                      (AIF)
                                    </p>
                                  </div>
                                }
                                wide="very"
                                position="top center"
                              />
                            </div>
                            <MySelectInput
                              name="clientThird.poaDisplayOption"
                              options={poaDisplayOptions}
                              placeholder="Select display option"
                            />
                          </Grid.Column>
                        </Grid.Row>
                        {values?.clientThird?.poaDisplayOption ===
                          "custom" && (
                          <Grid.Row className="zero top padding small bottom padding">
                            <Grid.Column width={16}>
                              <MyTextInput
                                name="clientThird.poaCustomDisplay"
                                label="Custom Signature Display"
                                placeholder="Enter how you want the signature to display"
                              />
                            </Grid.Column>
                          </Grid.Row>
                        )}
                      </>
                    )}
                  </>
                )}
              </Grid>
            </>
          )}
        </div>
      </Segment>
    );
  }

  function formImportMls() {
    return (
      <Segment className="background-lightgrey">
        <div className="tiny bottom margin">
          <h2 className="text blue">MLS Infomation</h2>
          <TransactionImportMlsForm mlsData={mlsData} setMlsData={setMlsData} />
        </div>
      </Segment>
    );
  }

  function formAddressDetails() {
    return (
      <Segment className="background-lightgrey">
        <div className="tiny bottom margin">
          <h2 className="text blue">Property Address</h2>
          <FormAddressAutofillGoogle />
        </div>
      </Segment>
    );
  }

  function formLegalDescription(values) {
    return (
      <Segment className="background-lightgrey">
        <div className="tiny bottom margin">
          <Grid>
            <Grid.Row>
              <Grid.Column mobile={16} computer={8}>
                <h2 className="text blue">County</h2>
                <MyTextInput name="propertyDetails.county" label="County" />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={16}>
                <h2 className="text blue">Legal Description</h2>
                <MyTextArea
                  rows="2"
                  name="propertyDetails.legalDescription"
                  label="Legal Description"
                />
              </Grid.Column>
              <Grid.Column width={16}>
                <TransactionImportLegalAddressForm
                  setLegalAddressData={setLegalAddressData}
                  address={values.address}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      </Segment>
    );
  }

  function formTimeZone() {
    return (
      <Segment className="background-lightgrey">
        <div className="tiny bottom margin">
          <Grid>
            <Grid.Row>
              <Grid.Column width={16}>
                <h2 className="text blue">Time Zone</h2>
                <MySelectInput
                  name="timeZone"
                  label="Time Zone"
                  placeholder="Select time zone"
                  options={TIMEZONE_OPTIONS}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      </Segment>
    );
  }

  function formTransactionName() {
    return (
      <Segment className="background-lightgrey">
        <div className="tiny bottom margin">
          <Grid>
            <Grid.Column mobile={16} computer={8}>
              <h2 className="text blue">Transaction Name</h2>
              <MyTextInput name="title" data-test="transactionTitle" />
            </Grid.Column>
          </Grid>
        </div>
      </Segment>
    );
  }

  function formButtons(isSubmitting, dirty) {
    return (
      <>
        <Grid>
          <Grid.Column className="zero top padding">
            <Divider className="large bottom margin" />
            <Button
              loading={isSubmitting}
              disabled={!dirty || isSubmitting}
              type="submit"
              floated={isMobile ? "left" : "right"}
              primary
              content="Submit"
              className={isMobile ? "fluid small bottom margin" : null}
              data-test="submit"
            />
            <Button
              disabled={isSubmitting}
              onClick={() =>
                dispatch(
                  closeModal({
                    modalType: "TransactionForm",
                  })
                )
              }
              to="#"
              type="button"
              floated={isMobile ? "left" : "right"}
              content="Cancel"
              className={isMobile ? "fluid" : null}
            />
          </Grid.Column>
        </Grid>
      </>
    );
  }

  return (
    <ModalWrapper size="large">
      <Segment clearing>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const modifiedMlsDataBasedOnMlsOptions = {
                ...mlsData,
                pic:
                  values?.mlsDataOptions?.useMlsPic === true && mlsData?.pic
                    ? mlsData.pic
                    : "",
              };
              if (values?.mlsDataOptions?.addListingAgentToParties === false) {
                delete modifiedMlsDataBasedOnMlsOptions.listingAgent;
              }

              const newTransaction = await addTransactionToDb(
                values,
                people,
                forms,
                currentUserProfile,
                modifiedMlsDataBasedOnMlsOptions,
                agentsForAssistant,
                [
                  ...(formTemplates || []).map(t => ({ ...t, templateSource: "own" })),
                  ...managerFormTemplates,
                ],
                allTaskTemplates,
                agentUserProfile // Pass agentUserProfile for manager or managerassistant creating transaction for agent
              );
              setSubmitting(false);
              toast.success("Transaction successfully created");
              dispatch(
                closeModal({
                  modalType: "TransactionForm",
                })
              );
              navigate(`/transactions/${newTransaction}/documents`);
            } catch (error) {
              toast.error(error.message);
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting, dirty, values, errors }) => (
            <Form className="ui form medium margin">
              <Watcher />
              <Header size="huge" color="blue">
                {transactionId ? "Edit Transaction" : "New Transaction"}
              </Header>
              <Divider />
              {(isAssistant || isTc) && formSelectAgent()}
              {formWhoRepresenting()}
              {/* {values.agentRepresents === "Seller" && <>{resLandCommForeclosure()}  </>} */}
              {resLandCommForeclosure()}
              {formClients(values)}
              {values.agentRepresents === "Buyer" && <>{formImportMls()}</>}
              {formAddressDetails()}
              {formLegalDescription(values)}
              {formTimeZone()}
              {formTransactionName()}
              {formButtons(isSubmitting, dirty)}

              {errors.client && (
                <Message
                  negative
                  className="float-right"
                  style={{ color: "red" }}
                >
                  {errors.client.email && <p>{errors.client.email}</p>}
                  {errors.client.lastName && <p>{errors.client.lastName}</p>}
                </Message>
              )}
            </Form>
          )}
        </Formik>
      </Segment>
    </ModalWrapper>
  );
}
